import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';  // Use updated AuthContext
import { db } from '../firebase';
import { doc, getDoc, setDoc }     from 'firebase/firestore';
import { TextField, Typography, Button, Avatar, Snackbar, Paper, CircularProgress, Grid, IconButton, Tooltip, InputAdornment } from '@mui/material';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';  // Import Firebase methods for re-authentication
import Visibility from '@mui/icons-material/Visibility';  // Import visibility icon
import VisibilityOff from '@mui/icons-material/VisibilityOff';  // Import visibility off icon

// Password criteria
const passwordCriteriaText = {
  minLength: 'At least 8 characters long',
  hasUpperCase: 'Includes at least one Uppercase letter',
  hasLowerCase: 'Includes at least one lowercase letter',
  hasNumbers: 'Includes at least one number',
  hasSpecialChars: 'Includes a special character',
};

const DEFAULT_PROFILE = { firstName: '', lastName: '', avatarUrl: '' };

const UserProfilePage = () => {
  const { currentUser, authLoading } = useAuth();
  const [profileData,        setProfileData]        = useState(DEFAULT_PROFILE);
  const [initialProfileData, setInitialProfileData] = useState(DEFAULT_PROFILE);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });
  
  // Individual loading states for each action
  const [saveLoading, setSaveLoading] = useState(false);  // Loading for saving preferences
  const [verifyLoading, setVerifyLoading] = useState(false);  // Loading for verifying current password
  const [passwordLoading, setPasswordLoading] = useState(false);  // Loading for changing password
  
  const [isModified, setIsModified] = useState(false); // Track if any changes are made
  const [toast, setToast] = useState({ open: false, message: '' });
  const [passwordErrors, setPasswordErrors] = useState([]);  // Track password validation errors
  const [canChangePassword, setCanChangePassword] = useState(false); // Enable/disable password change
  const [isCurrentPasswordValid, setIsCurrentPasswordValid] = useState(false); // Validate current password
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);  // Track when to show password criteria

  // Fetch user profile from Firestore when currentUser is available
  useEffect(() => {
    if (authLoading || !currentUser) return;  // Wait for the auth to load fully

    const fetchUserData = async () => {
      const userRef = doc(db, 'accounts', currentUser.uid);
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          // Merge so missing fields stay **defined** (''), keeping inputs controlled
          const userData = { ...DEFAULT_PROFILE, ...userSnap.data() };
          setProfileData(userData);
          setInitialProfileData(userData);
        } else {
          // First-time user → populate with defaults (no warning, no error)
         setProfileData(DEFAULT_PROFILE);
          setInitialProfileData(DEFAULT_PROFILE);
        }

      } catch (error) {
        setToast({ open: true, message: error.message });
      }
    };

    fetchUserData();
  }, [currentUser, authLoading]);

  // Compare the current profile data with the initial profile data
  useEffect(() => {
    const isProfileModified =
      initialProfileData.firstName !== profileData.firstName ||
      initialProfileData.lastName !== profileData.lastName ||
      initialProfileData.avatarUrl !== profileData.avatarUrl;

    setIsModified(isProfileModified);  // Enable save button only if there are modifications
  }, [profileData, initialProfileData]);

  // Validate password against criteria
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('minLength');
    if (!/[A-Z]/.test(password)) errors.push('hasUpperCase');
    if (!/[a-z]/.test(password)) errors.push('hasLowerCase');
    if (!/[0-9]/.test(password)) errors.push('hasNumbers');
    if (!/[^a-zA-Z0-9]/.test(password)) errors.push('hasSpecialChars');
    return errors;
  };

  // Validate password and update the state of form controls
  useEffect(() => {
    const errors = validatePassword(passwordData.newPassword);
    setPasswordErrors(errors);

    const isPasswordValid = errors.length === 0;
    const passwordsMatch = passwordData.newPassword === passwordData.confirmPassword;
    setCanChangePassword(isPasswordValid && passwordsMatch && passwordData.newPassword !== '');
  }, [passwordData]);

  // Function to handle profile data changes (e.g., First Name, Last Name)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!currentUser || saveLoading || !isModified) return; // Don't proceed if loading or no changes

    setSaveLoading(true);  // Local loading for saving the profile
    const userRef = doc(db, 'accounts', currentUser.uid);
    try {
      await setDoc(userRef, profileData, { merge: true });
      setInitialProfileData(profileData);  // Update initial profile after successful save
      setToast({ open: true, message: 'Profile updated successfully.' });
    } catch (error) {
      setToast({ open: true, message: 'Error updating profile: ' + error.message });
    } finally {
      setSaveLoading(false);  // Stop local loading after the save action is complete
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));

    if (name === 'newPassword') {
      setShowPasswordCriteria(value.length > 0);  // Show criteria when user starts typing
    }
  };

  // Validate current password with Firebase authentication
  const validateCurrentPassword = async () => {
    setVerifyLoading(true);  // Only show spinner for current password verification
    const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
    try {
      await reauthenticateWithCredential(currentUser, credential);
      setIsCurrentPasswordValid(true);  // Current password is valid
      setToast({ open: true, message: "Current password verified." });
    } catch (error) {
      setIsCurrentPasswordValid(false);  // Invalid current password
      setToast({ open: true, message: "Current password is incorrect." });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordLoading || !canChangePassword) return;  // Prevent action if invalid
    setPasswordLoading(true);  // Local loading for password change
    try {
      await currentUser.updatePassword(passwordData.newPassword);  // Firebase password update
      setToast({ open: true, message: "Password successfully updated." });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });  // Clear password fields
    } catch (error) {
      console.error("Password update failed", error);
      setToast({ open: true, message: `Error updating password: ${error.message}` });
    } finally {
      setPasswordLoading(false);  // Stop local loading after the password change action is complete
    }
  };

    // Toggle password visibility for current, new, or confirm passwords
    const togglePasswordVisibility = (field) => {
      setShowPassword((prev) => ({
        ...prev,
        [field]: !prev[field],  // Toggle the visibility for the specific field
      }));
    };


  const getPasswordField = (label, name, value, show, disabled = false) => (
    <TextField
      label={label}
      type={show ? 'text' : 'password'}
      name={name}
      value={value}
      onChange={handlePasswordChange}
      fullWidth
      disabled={disabled}  // Disable the field based on the `disabled` prop
      error={!disabled && name === 'newPassword' && passwordErrors.length > 0}  // Only show error when not disabled
      helperText={!disabled && name === 'newPassword' && passwordErrors.length > 0 ? 'Password does not meet criteria' : ''}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={`toggle ${name} visibility`}
            onClick={() => togglePasswordVisibility(name === 'currentPassword' ? 'current' : name === 'newPassword' ? 'new' : 'confirm')}  // Pass correct key
              edge="end"
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileData(prev => ({ ...prev, avatarUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Conditionally render loading or content based on authLoading
  if (authLoading) {
    return <CircularProgress />;  // Centralized loading spinner when authentication is still in progress
  }

  return (
    <Paper sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Profile
      </Typography>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Personal Info
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Click to change avatar">
            <IconButton color="primary" component="label">
              <Avatar src={profileData.avatarUrl} sx={{ width: 56, height: 56 }} />
              <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item xs={5} sm={5}>
          <TextField
            label="First Name"
            variant="outlined"
            name="firstName"
            value={profileData.firstName ?? ''}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={5} sm={5}>
          <TextField
            label="Last Name"
            variant="outlined"
            name="lastName"
            value={profileData.lastName ?? ''}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isModified || saveLoading} // Disable if no changes or loading
            sx={{ mt: 2 }}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
            Security & Authentication
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {getPasswordField("Current Password", "currentPassword", passwordData.currentPassword, showPassword.current)}
          <Button
            variant="contained"
            color="primary"
            disabled={!passwordData.currentPassword || verifyLoading}
            onClick={validateCurrentPassword}
            sx={{ mt: 1 }}
          >
            {verifyLoading ? <CircularProgress size={24} /> : 'Verify Password'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          {getPasswordField(
            "New Password", 
            "newPassword", 
            passwordData.newPassword, 
            showPassword.new, 
            !isCurrentPasswordValid  // Disable the New Password field if current password is not verified
          )}
          {/* Show password criteria below the new password field */}
          {showPasswordCriteria && (
            <ul style={{ fontSize: '12px', margin: '8px 0 0 0', paddingLeft: '20px', listStyleType: 'circle' }}>
              {Object.keys(passwordCriteriaText).map((criteriaKey) => (
                <li key={criteriaKey} style={{ color: passwordErrors.includes(criteriaKey) ? 'red' : 'green' }}>
                  {passwordCriteriaText[criteriaKey]}
                </li>
              ))}
            </ul>
          )}
        </Grid>
<Grid item xs={12}>
  {getPasswordField(
    "Confirm New Password", 
    "confirmPassword", 
    passwordData.confirmPassword, 
    showPassword.confirm, 
    passwordErrors.length > 0 || !passwordData.newPassword || passwordErrors.length > 0 // Ensure it's only disabled if new password is invalid
  )}
  {/* Show error if passwords do not match */}
  {passwordData.newPassword && passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
    <Typography variant="body2" color="error">
      Passwords do not match.
    </Typography>
  )}
</Grid>

        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            disabled={passwordLoading || !canChangePassword} // Disable if loading or invalid password
            sx={{ mt: 2 }}
          >
            {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message={toast.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default UserProfilePage;
